howspace.popover = (() => {
    'use strict';

    let FloatingUIDOM;
    let floatingUiLoad = null;
    const loadFloatingUI = () => {
        if (!floatingUiLoad) {
            floatingUiLoad = lib.module('@floating-ui/dom', 'floating-ui.dom.js')
                .then((m) => { FloatingUIDOM = m; })
                .catch((error) => {
                    // Clear the memo so the next open() retries instead of failing for the rest of the page session.
                    floatingUiLoad = null;
                    throw error;
                });
        }
        return floatingUiLoad;
    };

    // Module scoped so that ids are never reused. close() wipes the element's whole data bucket, so a counter
    // stored per element would restart from 1 and a stale open() could match the id of a newer one.
    let openSeq = 0;

    // Both names mean there is nothing to show or hide, e.g. the element was removed from the DOM or replaced
    // mid toggle. Firefox reports InvalidStateError where Chrome reports NotSupportedError. Anything else is a
    // real bug, and reportError surfaces it to the global error logger without breaking the caller.
    const surfaceUnexpectedToggleError = (e) => {
        if (e.name !== 'InvalidStateError' && e.name !== 'NotSupportedError') {
            reportError(e);
        }
    };

    const dataStorage = {};
    const getID = (elem) => {
        if (elem instanceof jQuery) {
            elem = elem[0];
        }
        if (!elem.id) {
            elem.id = 'popover-' + real.uid();
        }
        return elem.id;
    };
    const getData = (elem, key) => {
        const id = getID(elem);
        if (!dataStorage[id]) {
            dataStorage[id] = {};
        }
        return dataStorage[id][key] ?? null;
    };
    const setData = (elem, key, data) => {
        const id = getID(elem);
        if (!dataStorage[id]) {
            dataStorage[id] = {};
        }
        dataStorage[id][key] = data;
    };
    const removeData = (elem, key) => {
        const id = getID(elem);
        if (dataStorage[id]) {
            delete dataStorage[id][key];
        }
    };

    const clearAutoUpdate = (elem) => {
        if (elem instanceof jQuery) {
            elem = elem[0];
        }
        const autoUpdate = getData(elem, 'auto-update');
        if (typeof autoUpdate === 'function') {
            autoUpdate();
        }
        removeData(elem, 'auto-update');
    };

    const isOutsideNormalDocumentFlow = (elem) => {
        if (elem.parentElement) {
            const position = getComputedStyle(elem.parentElement).position;
            if (position === 'absolute' || position === 'fixed' || position === 'sticky') {
                return true;
            }
            return isOutsideNormalDocumentFlow(elem.parentElement);
        }
        return false;
    };

    const getMiddlewares = (flipOptions, margin, arrow, allowScroll, popoverScrollElem, popover) => {
        const middlewares = [
            FloatingUIDOM.flip(flipOptions),
            FloatingUIDOM.offset(margin),
            FloatingUIDOM.shift({padding: margin})
        ];

        if (arrow !== null) {
            middlewares.push(FloatingUIDOM.arrow({element: arrow, padding: 16}));
        }

        if (allowScroll) {
            if (popoverScrollElem === null) {
                popoverScrollElem = popover;
            }
            popoverScrollElem = howspace.getDOMElement(popoverScrollElem);
            middlewares.push(FloatingUIDOM.size({
                boundary: document.body,
                apply({availableWidth, availableHeight}) {
                    // Reserve space for sibling content (e.g. a menu-header) outside popoverScrollElem.
                    const siblingContentHeight = popover.offsetHeight - popoverScrollElem.offsetHeight;

                    // Sometimes the size middleware reports the available height as 0 or below.
                    // In that case we don't want to set the max height to a negative value.
                    const availableHeightWithMargin = availableHeight - 16 - siblingContentHeight;
                    if (availableHeightWithMargin > 0) {
                        Object.assign(popoverScrollElem.style, {
                            maxWidth: `${availableWidth}px`,
                            maxHeight: `${availableHeightWithMargin}px`,
                        });
                    }
                }
            }));
        }

        return middlewares;
    };

    const open = async (elem, popover, popoverScrollElem = null) => {
        elem = howspace.getDOMElement(elem);
        popover = howspace.getDOMElement(popover);

        // Loading the positioning library yields to the event loop on the first open of the page. Number each
        // open() call and record the newest number on the element. If the recorded number has changed after the
        // wait, the popover was closed or reopened meanwhile (close() deletes the element's data storage), and
        // this outdated call must not apply its stale state.
        const popoverId = ++openSeq;
        setData(elem, 'popover-id', popoverId);

        await loadFloatingUI();

        if (getData(elem, 'popover-id') !== popoverId) {
            return;
        }

        if (elem.classList.contains('popover-open')) {
            // This popover is already open. Just update its position.
            const updatePosition = getData(elem, 'update-position');
            if (typeof updatePosition === 'function') {
                updatePosition();
            }
            return;
        }

        elem.classList.add('popover-open');
        if (howspace.isFeatureSupported('popover')) {
            popover.setAttribute('popover', 'manual');
        }
        clearAutoUpdate(elem);

        const arrow = popover.querySelector('.arrow');
        const arrowShadow = popover.querySelector('.arrow-shadow');
        const margin = 8; // leave minimum of .5rem units margin
        const flipOptions = {};
        const placement = elem.dataset.placement ?? 'bottom';
        const alignment = elem.dataset.alignment ?? 'center';
        const hasNonStaticParent = isOutsideNormalDocumentFlow(elem);
        const isInsideTopMenu = elem.closest('#top') !== null;
        const allowVerticalFlip = elem.dataset.flipVertical === 'true' || (hasNonStaticParent && !isInsideTopMenu);
        const allowHorizontalFlip = elem.dataset.flipHorizontal !== 'false';
        const allowScroll = elem.dataset.scroll === 'true' || hasNonStaticParent;

        if (!allowVerticalFlip) {
            if (placement === 'top' || placement === 'bottom') {
                flipOptions.mainAxis = false;
            } else {
                flipOptions.crossAxis = false;
            }
        }

        if (!allowHorizontalFlip) {
            if (placement === 'left' || placement === 'right') {
                flipOptions.mainAxis = false;
            } else {
                flipOptions.crossAxis = false;
            }
        }

        const options = {
            placement: placement + '-' + alignment,
            middleware: getMiddlewares(flipOptions, margin, arrow, allowScroll, popoverScrollElem, popover),
        };

        const updatePosition = () => {
            Object.assign(popover.style, {
                maxWidth: '',
                maxHeight: '',
            });
            FloatingUIDOM.computePosition(elem, popover, options).then(data => {
                Object.assign(popover.style, {
                    left: `${data.x}px`,
                    top: `${data.y}px`,
                });

                if (arrow !== null) {
                    const arrowX = data.middlewareData.arrow.x || null;
                    const arrowY = data.middlewareData.arrow.y || null;
                    const staticSide = {
                        top: 'bottom',
                        right: 'left',
                        bottom: 'top',
                        left: 'right',
                    }[data.placement.split('-')[0]];

                    const styles = {
                        left: arrowX !== null ? `${arrowX}px` : '',
                        top: arrowY !== null ? `${arrowY}px` : '',
                        right: '',
                        bottom: '',
                        [staticSide]: '-5px',
                    };
                    Object.assign(arrow.style, styles);
                    Object.assign(arrowShadow.style, styles);
                }
            });
        };

        updatePosition();
        setData(elem, 'update-position', updatePosition);
        setData(elem, 'auto-update', FloatingUIDOM.autoUpdate(elem, popover, updatePosition));

        // TODO: here we could register the popover to a ResizeObserver to execute updatePosition whenever the popover
        // size changes (if performance becomes a problem then with a brief timeout to limit the number of updates).

        if (howspace.isFeatureSupported('popover')) {
            try {
                popover.showPopover();
            } catch (e) {
                surfaceUnexpectedToggleError(e);
            }
        }

        howspace.dispatchEvent(elem, howspace.popover.POPOVER_OPENED);
    }

    const close = (elem, popover) => {
        elem = howspace.getDOMElement(elem);
        popover = howspace.getDOMElement(popover);

        // Record which open() this close targets. The body below runs deferred, so a newer open() may stamp the
        // element while this close is still queued. That makes this close stale, and it must not tear down the
        // newer popover's state.
        const closeId = getData(elem, 'popover-id');

        real.onTierReady(real.TIER_2, () => {
            if (getData(elem, 'popover-id') !== closeId) {
                return;
            }

            clearAutoUpdate(elem);
            delete dataStorage[getID(elem)];

            if (elem.classList.contains('popover-open')) {
                elem.classList.remove('popover-open');
                // popover is null when the caller's node lookup came up empty, then there is nothing to hide.
                if (popover && howspace.isFeatureSupported('popover')) {
                    try {
                        popover.hidePopover();
                    } catch (e) {
                        surfaceUnexpectedToggleError(e);
                    }
                }
                howspace.dispatchEvent(elem, howspace.popover.POPOVER_CLOSED);
            }
        });
    };

    return {
        POPOVER_OPENED: 'popover-opened',
        POPOVER_CLOSED: 'popover-closed',
        open: open,
        close: close
    };
})();
